import { useCallback, useRef, useState, type TouchEvent } from "react";

const TRIGGER_DISTANCE = 80;
const MAX_DISTANCE = 160;

type PullToRefreshResult = {
  pulling: boolean;
  refreshing: boolean;
  distance: number;
  touchProps: Record<string, unknown>;
};

/**
 * Touch-first pull-to-refresh. The gesture only starts when the window is
 * already scrolled to the top, so vertical scrolling is never hijacked. There
 * is no desktop affordance on purpose — the pages expose `refreshing` so a
 * spinner can be rendered while the callback runs.
 */
export const usePullToRefresh = (
  onRefresh: () => Promise<void> | void,
): PullToRefreshResult => {
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [distance, setDistance] = useState(0);
  const startYRef = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const pullingRef = useRef(false);
  const refreshingRef = useRef(false);

  const onTouchStart = useCallback((event: TouchEvent) => {
    if (refreshingRef.current) return;
    if (window.scrollY > 0) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    startYRef.current = touch.clientY;
    distanceRef.current = 0;
    pullingRef.current = true;
  }, []);

  const onTouchMove = useCallback((event: TouchEvent) => {
    if (startYRef.current === null || !pullingRef.current) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    const delta = touch.clientY - startYRef.current;
    if (delta <= 0) {
      pullingRef.current = false;
      distanceRef.current = 0;
      setPulling(false);
      setDistance(0);
      return;
    }
    const next = Math.min(delta, MAX_DISTANCE);
    distanceRef.current = next;
    setDistance(next);
    setPulling(true);
  }, []);

  const onTouchEnd = useCallback(async () => {
    const dragged = distanceRef.current;
    const shouldRefresh =
      startYRef.current !== null &&
      pullingRef.current &&
      dragged >= TRIGGER_DISTANCE &&
      !refreshingRef.current;

    startYRef.current = null;
    distanceRef.current = 0;
    pullingRef.current = false;
    setPulling(false);
    setDistance(0);

    if (!shouldRefresh) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  }, [onRefresh]);

  return {
    pulling,
    refreshing,
    distance,
    touchProps: { onTouchStart, onTouchMove, onTouchEnd },
  };
};

export default usePullToRefresh;
